/**
 * Recurring changes page — shows diff between the latest and prior scan runs.
 *
 * Surfaces: score delta, rerun decision, template changes, issue group changes.
 */

import { useParams, Link } from "react-router-dom";
import {
  useLatestRecurring,
  useRecurringChanges,
  useRecurringAlert,
} from "../hooks/use-recurring.js";
import { useFirstOrg } from "../hooks/use-orgs.js";
import { useWebsites } from "../hooks/use-websites.js";
import { useBillingSummary } from "../hooks/use-billing.js";
import { Breadcrumbs } from "../components/breadcrumbs.js";
import { WarningPanel } from "../components/warning-panel.js";
import { ChangeIssueGroupList } from "../components/issue-group-list.js";
import { RegressionAlertBanner } from "../components/recurring/index.js";
import { PageSkeleton } from "../components/skeleton.js";
import type { TemplateChangeSummary } from "../api/product-types.js";

function TemplateDiffList({ changes }: { changes: TemplateChangeSummary[] }) {
  if (changes.length === 0)
    return <p className="text-muted">No template changes.</p>;

  return (
    <ul className="template-list" aria-label="Template changes">
      {changes.map((t) => (
        <li
          key={t.templateLabel}
          className={`template-row template-row--${t.changeType}`}
        >
          <div className="template-row__head">
            <span className="template-row__label">{t.templateLabel}</span>
            <span className={`change-badge change-badge--${t.changeType}`}>
              {t.changeType}
            </span>
            {t.significantDrift && (
              <span className="change-badge change-badge--drift">
                Significant drift
              </span>
            )}
          </div>
          {t.driftPercent != null && (
            <div className="template-row__meta text-sm text-muted">
              Fingerprint drift: {t.driftPercent.toFixed(1)}%
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

export function RecurringPage() {
  const { websiteId } = useParams<{ websiteId: string }>();
  const org = useFirstOrg();
  const { data: websitesData } = useWebsites(org?.id);
  const website =
    websitesData?.websites.find((w) => w.id === websiteId) ?? null;
  const { data: summaryData } = useBillingSummary(org?.id);
  const recurringEnabled = summaryData?.summary?.limits?.recurringEnabled ?? false;

  const { data: run, isLoading: runLoading, isError: runError } = useLatestRecurring(websiteId);
  const { data: changes, isLoading: changesLoading } = useRecurringChanges(
    websiteId,
    run?.id,
  );
  const { data: alert, isLoading: alertLoading } = useRecurringAlert(
    websiteId,
    run?.id,
  );

  const loading =
    runLoading || (run != null && (changesLoading || alertLoading));

  if (loading) return <PageSkeleton />;

  if (!run) {
    return (
      <div className="product-page">
        <Breadcrumbs
          items={[
            { label: "Websites", to: "/websites" },
            { label: website?.domain ?? "Website", to: `/websites/${websiteId}` },
            { label: "Monitoring" },
          ]}
        />
        <h1 className="page-title">Monitoring</h1>
        {runError ? (
          <div className="alert alert--error" role="alert">
            Failed to load monitoring data. Try refreshing the page.
          </div>
        ) : !recurringEnabled ? (
          <>
            <p className="text-muted">
              Scan-over-scan regression detection is available on the Pro plan
              and above. Upgrade to track score changes, new issues, and
              resolved issues across scans.
            </p>
            <Link to="/billing" className="btn btn--primary mt-2">
              View plans
            </Link>
          </>
        ) : (
          <>
            <p className="text-muted">
              No comparison data yet. After you have run at least two scans,
              this page shows what has changed — new issues, resolved issues,
              and score trends over time.
            </p>
            <Link
              to={`/websites/${websiteId}/scan`}
              className="btn btn--primary mt-2"
            >
              Run a scan
            </Link>
          </>
        )}
      </div>
    );
  }

  const rerunDecisionLabel: Record<string, string> = {
    lightweight_recheck: "Lightweight recheck recommended",
    full_rerun: "Full rerun recommended",
  };

  return (
    <div className="product-page">
      <Breadcrumbs
        items={[
          { label: "Websites", to: "/websites" },
          { label: website?.domain ?? "Website", to: `/websites/${websiteId}` },
          { label: "Monitoring" },
        ]}
      />
      <h1 className="page-title">Monitoring</h1>
      <p className="page-subtitle">
        Scan-over-scan changes between the latest and prior runs.
      </p>

      {/* ── Regression alert banner ────────────────────────────────────────── */}
      {alert && (
        <section className="product-section">
          <RegressionAlertBanner alert={alert} />
        </section>
      )}

      {/* ── Score delta ────────────────────────────────────────────────────── */}
      <section className="product-section" aria-labelledby="score-delta-h">
        <h2 id="score-delta-h" className="section-title">
          Score comparison
        </h2>
        <div className="stat-grid">
          <div className="stat-item">
            <span className="stat-item__value">
              {run.scorePrevious != null ? Math.round(run.scorePrevious) : "—"}
            </span>
            <span className="stat-item__label">Previous score</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">
              {run.scoreCurrent != null ? Math.round(run.scoreCurrent) : "—"}
            </span>
            <span className="stat-item__label">Current score</span>
          </div>
          {run.scoreDelta != null && (
            <div className="stat-item">
              <span
                className={`stat-item__value ${run.scoreDelta >= 0 ? "text-success" : "text-danger"}`}
              >
                {run.scoreDelta >= 0 ? "+" : ""}
                {run.scoreDelta.toFixed(1)}
              </span>
              <span className="stat-item__label">Delta</span>
            </div>
          )}
        </div>
      </section>

      {/* ── Rerun decision ─────────────────────────────────────────────────── */}
      {run.rerunDecision && (
        <section className="product-section" aria-labelledby="rerun-h">
          <h2 id="rerun-h" className="section-title">
            Rerun recommendation
          </h2>
          <p
            className={`alert ${run.rerunDecision === "full_rerun" ? "alert--warning" : "alert--info"}`}
          >
            {rerunDecisionLabel[run.rerunDecision] ?? run.rerunDecision}
          </p>
        </section>
      )}

      {/* ── Warnings ───────────────────────────────────────────────────────── */}
      {run.warnings.length > 0 && (
        <section className="product-section">
          <WarningPanel warnings={run.warnings} label="Comparison notices" />
        </section>
      )}

      {/* ── Template changes ───────────────────────────────────────────────── */}
      <section className="product-section" aria-labelledby="tpl-changes-h">
        <h2 id="tpl-changes-h" className="section-title">
          Template changes
        </h2>
        <div className="stat-grid mb-3">
          <div className="stat-item">
            <span className="stat-item__value">{run.templatesNew}</span>
            <span className="stat-item__label">New</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">{run.templatesChanged}</span>
            <span className="stat-item__label">Changed</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">{run.templatesRemoved}</span>
            <span className="stat-item__label">Removed</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">{run.templatesUnchanged}</span>
            <span className="stat-item__label">Unchanged</span>
          </div>
        </div>
        {changes && <TemplateDiffList changes={changes.templateChanges} />}
      </section>

      {/* ── Issue group changes ────────────────────────────────────────────── */}
      <section className="product-section" aria-labelledby="issue-changes-h">
        <h2 id="issue-changes-h" className="section-title">
          Issue group changes
        </h2>
        <div className="stat-grid mb-3">
          <div className="stat-item">
            <span className="stat-item__value">{run.issueGroupsNew}</span>
            <span className="stat-item__label">New</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">{run.issueGroupsResolved}</span>
            <span className="stat-item__label">Resolved</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">{run.issueGroupsUnchanged}</span>
            <span className="stat-item__label">Unchanged</span>
          </div>
        </div>
        {changes && <ChangeIssueGroupList groups={changes.issueGroupChanges} />}
      </section>

      {/* ── Related pages ────────────────────────────────────────────────── */}
      <section className="product-section">
        <div className="action-links">
          <Link
            to={`/websites/${websiteId}`}
            className="btn btn--secondary"
          >
            Dashboard
          </Link>
          <Link
            to={`/websites/${websiteId}/report`}
            className="btn btn--secondary"
          >
            Full report
          </Link>
          <Link
            to={`/websites/${websiteId}/scan`}
            className="btn btn--secondary"
          >
            Run a new scan
          </Link>
        </div>
      </section>
    </div>
  );
}
